import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MdArrowBack, MdArrowForward, MdSave, MdImage, MdLayers } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import Card from "components/card";
import FileUploader, { type UploadedFile } from "components/ui/FileUploader";
import MultiSelect from "components/ui/MultiSelect";
import Select from "components/ui/Select";
import { toastError } from "lib/toast";
import { instructorCoursesService, type InstructorCourse, resolveCoursePricingType } from "services/instructorCoursesService";
import { instructorBundlesService, type BundleUpsertBody } from "services/instructorBundlesService";

type PricingType = "free" | "paid";

function coursePrice(course: InstructorCourse): number {
  return resolveCoursePricingType(course) === "paid" ? Number(course.price ?? 0) : 0;
}

export default function BundleFormPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();
  const { bundleId } = useParams();
  const isEdit = Boolean(bundleId);

  const [courses, setCourses] = React.useState<InstructorCourse[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [image, setImage] = React.useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [pricingType, setPricingType] = React.useState<PricingType>("paid");
  const [price, setPrice] = React.useState("");
  const [currency, setCurrency] = React.useState("EGP");
  const [isActive, setIsActive] = React.useState(true);
  const [startsAt, setStartsAt] = React.useState("");
  const [endsAt, setEndsAt] = React.useState("");

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const list = await instructorCoursesService.list({ limit: 500 } as any);
        setCourses(Array.isArray(list.data) ? list.data : []);

        if (isEdit && bundleId) {
          const bundle = await instructorBundlesService.get(bundleId);
          if (bundle) {
            setTitle(bundle.title ?? "");
            setDescription(bundle.description ?? "");
            setImage(bundle.image ?? null);
            setImagePreviewUrl(bundle.image_url ?? null);
            setSelectedIds((bundle.courses ?? []).map((c) => Number(c.id)));
            setPricingType((bundle.pricing_type as PricingType) || "paid");
            setPrice(bundle.price != null ? String(bundle.price) : "");
            setCurrency(bundle.currency ?? "EGP");
            setIsActive(bundle.is_active ?? true);
            setStartsAt(bundle.starts_at ? bundle.starts_at.slice(0, 10) : "");
            setEndsAt(bundle.ends_at ? bundle.ends_at.slice(0, 10) : "");
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [bundleId, isEdit]);

  const courseOptions = React.useMemo(
    () =>
      courses.map((c) => ({
        value: c.id,
        label: c.title,
        description:
          resolveCoursePricingType(c) === "paid"
            ? `${Number(c.price ?? 0)} ${c.currency || "EGP"}`
            : isArabic
              ? "مجاني"
              : "Free",
      })),
    [courses, isArabic]
  );

  const selectedCourses = React.useMemo(
    () => courses.filter((c) => selectedIds.includes(Number(c.id))),
    [courses, selectedIds]
  );

  const originalTotal = React.useMemo(
    () => selectedCourses.reduce((sum, c) => sum + coursePrice(c), 0),
    [selectedCourses]
  );

  const bundlePrice = pricingType === "paid" ? Number(price) || 0 : 0;
  const savings = Math.max(0, originalTotal - bundlePrice);
  const savingsPct = originalTotal > 0 ? Math.round((savings / originalTotal) * 100) : 0;

  const handleImageChange = (files: UploadedFile[]) => {
    const done = files.find((f) => f.status === "done" && f.key);
    if (done?.key) {
      setImage(done.key);
      setImagePreviewUrl(done.localPreviewUrl || done.url || null);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toastError(isArabic ? "العنوان مطلوب" : "Title is required");
      return;
    }
    if (selectedIds.length < 2) {
      toastError(isArabic ? "اختر كورسين على الأقل" : "Select at least 2 courses");
      return;
    }
    if (pricingType === "paid" && !(Number(price) > 0)) {
      toastError(isArabic ? "أدخل سعر عرض أكبر من صفر" : "Enter a bundle price greater than 0");
      return;
    }

    const body: BundleUpsertBody = {
      title: title.trim(),
      description: description.trim() || null,
      image: image || null,
      course_ids: selectedIds,
      pricing_type: pricingType,
      price: pricingType === "paid" ? Number(price) : 0,
      currency: currency || "EGP",
      is_active: isActive,
      starts_at: startsAt || null,
      ends_at: endsAt || null,
    };

    setSaving(true);
    try {
      if (isEdit && bundleId) {
        await instructorBundlesService.update(bundleId, body);
      } else {
        await instructorBundlesService.create(body);
      }
      navigate("..");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-5 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <Link to=".." className="inline-flex items-center gap-1 text-sm font-semibold text-brand-500 hover:underline">
          {isArabic ? <MdArrowForward className="h-4 w-4" /> : <MdArrowBack className="h-4 w-4" />}
          {isArabic ? "العودة للعروض" : "Back to bundles"}
        </Link>
      </div>

      <Card extra="w-full p-4 md:p-6">
        <header className="mb-4">
          <h1 className="text-xl font-bold text-navy-700 dark:text-white">
            {isEdit ? (isArabic ? "تعديل العرض" : "Edit Bundle") : isArabic ? "إنشاء عرض مجمع" : "Create Bundle"}
          </h1>
          <p className="text-sm text-gray-500">
            {isArabic
              ? "اجمع كورساتك في عرض واحد بسعر مخفّض."
              : "Combine your courses into a single discounted offer."}
          </p>
        </header>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-500">{isArabic ? "جارٍ التحميل..." : "Loading..."}</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-2">
              {isArabic ? "العنوان *" : "Title *"}
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={isArabic ? "مثلاً: باقة التفوق" : "e.g. Excellence Bundle"}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </label>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-2">
              {isArabic ? "الوصف" : "Description"}
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </label>

            <div className="md:col-span-2">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {isArabic ? "كورسات العرض * (كورسان على الأقل)" : "Bundle courses * (at least 2)"}
              </p>
              <MultiSelect
                value={selectedIds}
                options={courseOptions}
                onChange={(next) => setSelectedIds(next.map(Number))}
                placeholder={isArabic ? "اختر كورساتك" : "Select your courses"}
                className="mt-1"
              />
            </div>

            {selectedCourses.length > 0 && (
              <div className="md:col-span-2">
                <div className="grid gap-2 sm:grid-cols-2">
                  {selectedCourses.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5"
                    >
                      <span className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                        <MdLayers className="h-4 w-4 text-brand-500" />
                        <span className="line-clamp-1">{c.title}</span>
                      </span>
                      <span className="font-semibold text-navy-700 dark:text-white">
                        {coursePrice(c)} {c.currency || "EGP"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {isArabic ? "نوع التسعير" : "Pricing type"}
              </p>
              <Select
                value={pricingType}
                onChange={(next) => setPricingType(((next as PricingType) || "paid"))}
                options={[
                  { value: "paid", label: isArabic ? "مدفوع" : "Paid" },
                  { value: "free", label: isArabic ? "مجاني" : "Free" },
                ]}
                isArabic={isArabic}
                allowEmpty={false}
                clearable={false}
                className="mt-1"
              />
            </div>

            {pricingType === "paid" && (
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {isArabic ? "سعر العرض *" : "Bundle price *"}
                <input
                  type="number"
                  min={0}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0"
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </label>
            )}

            <div className="md:col-span-2 grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm dark:border-white/10 dark:bg-white/5 sm:grid-cols-3">
              <div>
                <p className="text-xs text-gray-500">{isArabic ? "السعر الأصلي" : "Original Price"}</p>
                <p className="text-lg font-bold text-gray-700 line-through dark:text-gray-200">
                  {originalTotal} {currency}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isArabic ? "سعر العرض" : "Bundle Price"}</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white">
                  {bundlePrice} {currency}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isArabic ? "نسبة الخصم" : "Discount"}</p>
                <p className="text-lg font-bold text-green-600">
                  {savings} {currency} ({savingsPct}%)
                </p>
              </div>
            </div>

            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              {isArabic ? "تاريخ البداية (اختياري)" : "Start date (optional)"}
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </label>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              {isArabic ? "تاريخ النهاية (اختياري)" : "End date (optional)"}
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </label>

            <label className="md:col-span-2 flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              {isArabic ? "العرض مُفعّل" : "Bundle active"}
            </label>

            <div className="md:col-span-2">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {isArabic ? "صورة العرض" : "Bundle image"}
              </p>
              <div className="mt-1 flex flex-col gap-3 md:flex-row">
                <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-white/5 md:w-44">
                  {imagePreviewUrl ? (
                    <img src={imagePreviewUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <MdImage className="h-8 w-8" />
                  )}
                </div>
                <FileUploader
                  group="documents"
                  accept="image/*"
                  multiple={false}
                  onChange={handleImageChange}
                  className="flex-1"
                  hint={isArabic ? "مسموح بصور فقط" : "Images only"}
                />
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-white/10">
          <Link
            to=".."
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/10"
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </Link>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            <MdSave className="h-5 w-5" />
            {saving
              ? isArabic
                ? "جارٍ الحفظ..."
                : "Saving..."
              : isEdit
                ? isArabic
                  ? "حفظ التعديلات"
                  : "Save changes"
                : isArabic
                  ? "إنشاء العرض"
                  : "Create bundle"}
          </button>
        </div>
      </Card>
    </div>
  );
}
